import type { UserEntitlement } from '@/src/dal/user-dal'
import type { RestorePurchasesResult } from '@/src/services/entitlementService'
import type { QueryClient } from '@tanstack/react-query'
import type { Dispatch } from 'react'
import { ActionSheetIOS, Platform } from 'react-native'
import Purchases from 'react-native-purchases'
import { changeLanguageWithLocalResources } from '@/src/i18n'
import { setUserSelectedLanguage } from '@/src/i18n/languageDetector'
import { customerInfoQueryOptions } from '@/src/queries/account-queries'
import { resolveRestorePurchasesResult } from '@/src/services/entitlementService'
import { showAlert } from '@/src/utils/alert'
import { openExternalUrl } from '@/src/utils/external-url'
import { captureException, captureMessage } from '@/src/utils/sentry'

import type { SettingsUiAction } from './settingsScreenState'
import {
  getErrorMessage,
  getSubscriptionStore,
} from '../utils/settings-screen-helpers'

export type Translate = (
  key: string,
  options?: Record<string, unknown>,
) => string
export type CustomerInfo = Awaited<ReturnType<typeof Purchases.getCustomerInfo>>
export type EligibilityRefetch = () => Promise<{
  data?: { eligible?: boolean }
}>

export type ManageSubscriptionDestination =
  | { type: 'stripe_portal' }
  | { type: 'test_store' }
  | { type: 'management_url'; url: string }
  | { type: 'unavailable' }

export type UnsubscribeDestination =
  | { type: 'stripe_portal' }
  | { type: 'management_url'; url: string }
  | { type: 'unavailable' }

export function resolveManageSubscriptionDestination(
  info: CustomerInfo,
): ManageSubscriptionDestination {
  const store = getSubscriptionStore(info)

  if (store === 'STRIPE') {
    return { type: 'stripe_portal' }
  }

  if (store === 'TEST_STORE') {
    return { type: 'test_store' }
  }

  if (info.managementURL) {
    return { type: 'management_url', url: info.managementURL }
  }

  return { type: 'unavailable' }
}

export function resolveUnsubscribeDestination(
  info: CustomerInfo,
): UnsubscribeDestination {
  const store = getSubscriptionStore(info)

  if (store === 'STRIPE') {
    return { type: 'stripe_portal' }
  }

  if (info.managementURL) {
    return { type: 'management_url', url: info.managementURL }
  }

  return { type: 'unavailable' }
}

export async function applyLanguageSelection(
  language: 'en' | 'sv',
  onCloseLanguageOptions: () => void,
  onError: () => void,
) {
  try {
    if (Platform.OS === 'web') {
      onCloseLanguageOptions()
    }
    await setUserSelectedLanguage(language)
    await changeLanguageWithLocalResources(language)
  } catch (error) {
    console.error('Failed to update language preference', error)
    captureException(error, {
      feature: 'i18n',
      tags: {
        flow: 'settings_language_selection',
        language,
      },
    })
    onError()
  }
}

export async function openSettingsUrl(url: string) {
  try {
    await openExternalUrl(url)
  } catch (error) {
    console.error('Failed to open URL:', error)
  }
}

export async function manageSubscription({
  authUserId,
  queryClient,
  createPortalSession,
  dispatch,
  t,
}: {
  authUserId: string | null
  queryClient: QueryClient
  createPortalSession: { mutateAsync: () => Promise<string> }
  dispatch: Dispatch<SettingsUiAction>
  t: Translate
}) {
  dispatch({ type: 'setIsManagingSubscription', value: true })
  const cachedCustomerInfo = queryClient.getQueryData(
    customerInfoQueryOptions(authUserId).queryKey,
  ) as CustomerInfo | undefined

  try {
    const info = cachedCustomerInfo ?? (await Purchases.getCustomerInfo())
    const destination = resolveManageSubscriptionDestination(info)
    const store = getSubscriptionStore(info)
    const storeTag = store ?? 'UNKNOWN'

    switch (destination.type) {
      case 'stripe_portal': {
        const portalUrl = await createPortalSession.mutateAsync()
        await openSettingsUrl(portalUrl)
        break
      }
      case 'test_store':
        showAlert(
          t('profile:test_store_title'),
          t('profile:test_store_message'),
        )
        break
      case 'management_url':
        await openSettingsUrl(destination.url)
        break
      case 'unavailable':
        captureMessage('Manage subscription unavailable', 'info', {
          feature: 'settings',
          tags: {
            action: 'manage_subscription',
            result: 'no_management_url',
            store: storeTag,
          },
        })
        showAlert(
          t('profile:no_subscriptions_title'),
          t('profile:can_not_manage_subscription'),
        )
        break
    }
  } catch (error: unknown) {
    captureException(error, {
      feature: 'settings',
      tags: { action: 'manage_subscription', store: 'UNKNOWN' },
    })
    const errorMessage = getErrorMessage(error)
    showAlert(
      t('common:error'),
      errorMessage ?? t('profile:end_subscription_failed'),
    )
  } finally {
    dispatch({ type: 'setIsManagingSubscription', value: false })
  }
}

export async function continueAfterUnsubscribe({
  dispatch,
  refetchEligibility,
  t,
}: {
  dispatch: Dispatch<SettingsUiAction>
  refetchEligibility: EligibilityRefetch
  t: Translate
}) {
  dispatch({ type: 'setIsCheckingEligibility', value: true })
  try {
    const { data: refreshed } = await refetchEligibility()
    if (refreshed?.eligible) {
      dispatch({ type: 'setRemoveDialogStep', value: 'confirm' })
    } else {
      showAlert(
        t('profile:remove_account_blocked_title'),
        t('profile:remove_account_blocked_message'),
      )
    }
  } catch {
    showAlert(t('common:error'), t('profile:remove_account_failed'))
  }

  dispatch({ type: 'setIsCheckingEligibility', value: false })
}

export async function openUnsubscribeUrl({
  authUserId,
  queryClient,
  createCancelPortalSession,
  t,
}: {
  authUserId: string | null
  queryClient: QueryClient
  createCancelPortalSession: { mutateAsync: () => Promise<string> }
  t: Translate
}) {
  const cachedCustomerInfo = queryClient.getQueryData(
    customerInfoQueryOptions(authUserId).queryKey,
  ) as CustomerInfo | undefined

  try {
    const info = cachedCustomerInfo ?? (await Purchases.getCustomerInfo())
    const destination = resolveUnsubscribeDestination(info)

    switch (destination.type) {
      case 'stripe_portal': {
        const portalUrl = await createCancelPortalSession.mutateAsync()
        await openSettingsUrl(portalUrl)
        break
      }
      case 'management_url':
        await openSettingsUrl(destination.url)
        break
      case 'unavailable':
        showAlert(
          t('profile:no_subscriptions_title'),
          t('profile:no_subscriptions_message'),
        )
        break
    }
  } catch (error: unknown) {
    const errorMessage = getErrorMessage(error)
    showAlert(
      t('common:error'),
      errorMessage ?? t('profile:end_subscription_failed'),
    )
  }
}

export async function deleteAccount({
  deleteAccountMutation,
  authLogout,
  dispatch,
  t,
}: {
  deleteAccountMutation: { mutateAsync: () => Promise<unknown> }
  authLogout: () => Promise<unknown>
  dispatch: Dispatch<SettingsUiAction>
  t: Translate
}) {
  try {
    await deleteAccountMutation.mutateAsync()
    dispatch({ type: 'setShowRemoveAccountDialog', value: false })
    showAlert(t('common:info'), t('profile:remove_account_success'))
    await authLogout()
  } catch (error: unknown) {
    dispatch({ type: 'setShowRemoveAccountDialog', value: false })
    const errorMessage = getErrorMessage(error)
    showAlert(
      t('common:error'),
      errorMessage ?? t('profile:remove_account_failed'),
    )
  }
}

export function restorePurchases({
  revenueCatId,
  refreshUserStatus,
  refetchCustomerInfo,
  refetchUser,
  restorePurchasesMutation,
  t,
}: {
  revenueCatId: string | null | undefined
  refreshUserStatus: () => Promise<void>
  refetchCustomerInfo: () => Promise<{ data?: CustomerInfo }>
  refetchUser: () => Promise<{
    data?: { entitlement?: UserEntitlement | null } | null
  }>
  restorePurchasesMutation: {
    mutate: (
      value: undefined,
      options: {
        onSuccess: (restoredInfo: CustomerInfo) => void | Promise<void>
        onError: (error: unknown) => void
      },
    ) => void
  }
  t: Translate
}) {
  restorePurchasesMutation.mutate(undefined, {
    onSuccess: async (restoredInfo) => {
      const initialOutcome = resolveRestorePurchasesResult({
        customerInfo: restoredInfo,
        entitlement: null,
        revenueCatId,
      })

      if (initialOutcome.outcome === 'ownership_mismatch') {
        captureMessage('restore_ownership_mismatch', 'warning', {
          feature: 'purchase',
          tags: { flow: 'restore_purchases' },
          extra: {
            currentRevenueCatId:
              initialOutcome.currentRevenueCatId ?? 'missing',
            originalAppUserId: initialOutcome.originalAppUserId,
          },
        })
        showAlert(
          t('profile:restore_purchases_mismatch_title'),
          t('profile:restore_purchases_mismatch_message'),
        )
        return
      }

      let refreshedCustomerInfo = restoredInfo
      let refreshedEntitlement: UserEntitlement | null | undefined

      try {
        await refreshUserStatus()
        const [customerInfoResult, userResult] = await Promise.all([
          refetchCustomerInfo(),
          refetchUser(),
        ])
        refreshedCustomerInfo = customerInfoResult.data ?? restoredInfo
        refreshedEntitlement = userResult.data?.entitlement
      } catch (error: unknown) {
        captureException(error, {
          feature: 'purchase',
          tags: { flow: 'restore_refresh_status' },
        })
        const failedResult: RestorePurchasesResult = { outcome: 'error' }
        if (failedResult.outcome === 'error') {
          showAlert(t('common:error'), t('profile:restore_purchases_failed'))
        }
        return
      }

      const result = resolveRestorePurchasesResult({
        customerInfo: refreshedCustomerInfo,
        entitlement: refreshedEntitlement,
        revenueCatId,
      })

      switch (result.outcome) {
        case 'success':
          showAlert(t('common:success'), t('profile:restored_success'))
          break
        case 'ownership_mismatch':
          captureMessage(
            'restore_ownership_mismatch_after_refresh',
            'warning',
            {
              feature: 'purchase',
              tags: { flow: 'restore_purchases' },
              extra: {
                currentRevenueCatId: result.currentRevenueCatId ?? 'missing',
                originalAppUserId: result.originalAppUserId,
              },
            },
          )
          showAlert(
            t('profile:restore_purchases_mismatch_title'),
            t('profile:restore_purchases_mismatch_message'),
          )
          break
        case 'no_subscription':
          showAlert(
            t('profile:no_subscriptions_title'),
            t('profile:no_subscriptions_message'),
          )
          break
      }
    },
    onError: (error: unknown) => {
      const errorMessage = getErrorMessage(error)
      showAlert(
        t('common:error'),
        errorMessage ?? t('profile:restore_purchases_failed'),
      )
    },
  })
}

export async function openDeleteAccountDialog({
  refetchEligibility,
  dispatch,
}: {
  refetchEligibility: EligibilityRefetch
  dispatch: Dispatch<SettingsUiAction>
}) {
  const { data: freshEligibility } = await refetchEligibility()

  if (freshEligibility?.eligible !== true) {
    dispatch({ type: 'setRemoveDialogStep', value: 'unsubscribe' })
    dispatch({ type: 'setShowRemoveAccountDialog', value: true })
    return
  }

  dispatch({ type: 'setRemoveDialogStep', value: 'confirm' })
  dispatch({ type: 'setShowRemoveAccountDialog', value: true })
}

export function showLanguageChangeOptions({
  t,
  onShowWebModal,
  onSelectLanguage,
}: {
  t: Translate
  onShowWebModal: () => void
  onSelectLanguage: (language: 'en' | 'sv') => void
}) {
  if (Platform.OS === 'web') {
    onShowWebModal()
    return
  }

  const options = [
    t('profile:language_en'),
    t('profile:language_sv'),
    t('common:cancel'),
  ]
  const cancelButtonIndex = 2

  if (Platform.OS === 'ios') {
    ActionSheetIOS.showActionSheetWithOptions(
      {
        options,
        cancelButtonIndex,
        title: t('profile:change_language'),
      },
      (buttonIndex) => {
        if (buttonIndex === 0) {
          onSelectLanguage('en')
        } else if (buttonIndex === 1) {
          onSelectLanguage('sv')
        }
      },
    )
    return
  }

  showAlert(t('profile:change_language'), undefined, [
    {
      text: t('profile:language_en'),
      onPress: () => onSelectLanguage('en'),
    },
    {
      text: t('profile:language_sv'),
      onPress: () => onSelectLanguage('sv'),
    },
    { text: t('common:cancel'), style: 'cancel' },
  ])
}

export function saveEditedName({
  editNameValue,
  dispatch,
  updateUserMutation,
  t,
}: {
  editNameValue: string
  dispatch: Dispatch<SettingsUiAction>
  updateUserMutation: {
    isPending: boolean
    mutate: (
      value: { firstName: string },
      options: {
        onSuccess: () => void
        onError: (error: unknown) => void
      },
    ) => void
  }
  t: Translate
}) {
  const trimmed = editNameValue.trim()
  if (trimmed.length === 0) {
    dispatch({
      type: 'setEditNameError',
      value: t('profile:edit_name_empty_error'),
    })
    return
  }

  updateUserMutation.mutate(
    { firstName: trimmed },
    {
      onSuccess: () => {
        dispatch({ type: 'closeEditNameDialog' })
        showAlert(t('common:success'), t('profile:edit_name_success'))
      },
      onError: (error: unknown) => {
        const errorMessage =
          getErrorMessage(error) ?? t('profile:edit_name_failed')
        dispatch({ type: 'setEditNameError', value: errorMessage })
      },
    },
  )
}
